import { useTranslation } from 'react-i18next';

/**
 * Get a localized field value from an object with fallback logic.
 *
 * Database fields follow the pattern: field_name_en and field_name_fr
 *
 * Fallback strategy:
 * 1. Try to get the locale-specific field (e.g., title_fr)
 * 2. If the value is an empty string, null, undefined, or whitespace-only, fall back to English
 * 3. Return the English value as the final fallback
 *
 * @param obj - The object containing the localized fields
 * @param fieldBase - The base field name without the locale suffix (e.g., 'title' for 'title_en'/'title_fr')
 * @param locale - The locale code ('en' or 'fr')
 * @returns The localized field value or English fallback
 *
 * @example
 * const question = { title_en: 'Who will win?', title_fr: '' };
 * getLocalizedField(question, 'title', 'fr') // Returns 'Who will win?' (fallback to English)
 *
 * const question2 = { title_en: 'Who will win?', title_fr: 'Qui va gagner?' };
 * getLocalizedField(question2, 'title', 'fr') // Returns 'Qui va gagner?'
 */
export function getLocalizedField<T extends Record<string, any>>(
  obj: T,
  fieldBase: string,
  locale: string
): string {
  const frenchField = `${fieldBase}_fr`;
  const englishField = `${fieldBase}_en`;

  if (locale === 'fr') {
    const frenchValue = obj[frenchField];

    // Check if the Spanish value exists and is not empty/whitespace-only
    if (
      frenchValue !== null &&
      frenchValue !== undefined &&
      typeof frenchValue === 'string' &&
      frenchValue.trim() !== ''
    ) {
      return frenchValue;
    }

    // Fall back to English if Spanish is empty, null, undefined, or whitespace
    return obj[englishField] || '';
  }

  // Return English value for 'en' locale
  return obj[englishField] || '';
}

/**
 * Custom hook to get a localized field getter function based on the current i18n language.
 *
 * @returns A function that takes an object and field base and returns the localized value
 *
 * @example
 * import { useLocalizedFieldGetter } from '@/utils/localized-field';
 *
 * const getField = useLocalizedFieldGetter();
 * const questionText = getField(question, 'title');
 */
export function useLocalizedFieldGetter() {
  const { i18n } = useTranslation();

  return (obj: Record<string, any>, fieldBase: string) => {
    return getLocalizedField(obj, fieldBase, i18n.language);
  };
}
